// **React Imports
import { Fragment, memo, useCallback, useMemo, useState } from 'react'

// **MUI Imports
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { Link, useParams } from 'react-router-dom'

// **Components Imports
import Chip from '../../../../components/chip'
import Icon from '../../../../components/icon'
import ScoreBadge from '../../../../components/score-badge'

// **Utils
import {
  ATTEMPT_STATUS_LABEL,
  CANDIDATE_RESULT_STATUS,
  CHALLENGE_TYPES,
  stageStatus
} from '../../../../utils/constant'
import { TOOLTIPS } from '../../../../utils/tooltips'
import {
  checkPublicRoute,
  formatDateTime,
  shouldRenderPlaybackIcon
} from '../../../../utils/utils'
import AIScoreComponent from '../assessment-dashboard/components/AIScoreComponent'
import LinearityScoreComponent from '../stage/stage-details/leaderboard/components/LinearityScoreComponent'

// **API

// Componentns
const TableHeaderRow = memo(({ columns }) => (
  <TableRow>
    {columns.map((column) => (
      <TableCell
        key={column?.field}
        sx={{
          width: column?.width || 'auto',
          minWidth: column?.minWidth || '120px',
          ...(!column?.width ? { flex: 1 } : {})
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {column?.headerName || ''}
          {column?.tooltip ? (
            <Icon
              icon="mdi:information-outline"
              title={column?.tooltip}
              style={{
                fontSize: '16px',
                color: 'var(--text-secondary)'
              }}
            />
          ) : null}
        </Box>
      </TableCell>
    ))}
  </TableRow>
))

const ChildTable = memo(({ rowData }) => {
  // variables
  const columns = useMemo(() => {
    if (rowData?.type === CHALLENGE_TYPES.MCQ) {
      return [
        {
          field: 'topicName',
          headerName: 'Skill',
          display: 'flex',
          minWidth: '180px',
          sortable: false,
          flex: 0.2
        },
        {
          field: 'totalQuestions',
          headerName: '# Questions',
          display: 'flex',
          minWidth: '90px',
          sortable: true,
          flex: 0.2
        },
        {
          field: 'attempted',
          headerName: 'Attempted',
          display: 'flex',
          minWidth: '80px',
          sortable: true,
          flex: 0.2
        },

        {
          field: 'unattempted',
          headerName: 'Unattempted',
          display: 'flex',
          minWidth: '80px',
          sortable: true,
          flex: 0.2
        },

        {
          field: 'score',
          headerName: 'Score',
          display: 'flex',
          minWidth: '100px',
          flex: 0.2,
          sortable: true,
          renderCell: ({ row }) => {
            return row?.resultStatus ===
              CANDIDATE_RESULT_STATUS.PENDING_MANUAL ? (
              <Typography
                sx={{
                  color: 'var(--warning-main)',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                Yet to be scored
              </Typography>
            ) : row?.score == null ? (
              '-'
            ) : (
              <ScoreBadge acquired={row?.score} total={row?.totalScore} />
            )
          }
        },
        {
          field: 'percentage',
          headerName: 'Percentage',
          display: 'flex',
          minWidth: '100px',
          sortable: true,
          flex: 0.2,
          renderCell: ({ row }) => <>{row?.percentage || 0}%</>
        }
      ]
    }

    return [
      { field: 'challengeName', headerName: 'Challenge' },
      {
        field: 'duration',
        headerName: 'Challenge Duration',
        tooltip: TOOLTIPS.challengeDuration,
        renderCell: ({ row }) => (
          <Typography sx={{ fontSize: '12px' }}>
            {row?.duration != null ? `${row?.duration} mins` : '-'}
          </Typography>
        )
      },
      {
        field: 'score',
        headerName: 'Score',
        tooltip: TOOLTIPS.score,
        renderCell: ({ row }) =>
          row?.score != null ? (
            <ScoreBadge
              acquired={row?.score}
              total={row?.outOfScore}
              sx={{ '& .score-badge': { margin: '0 !important' } }}
            />
          ) : (
            '-'
          )
      },
      ...(rowData?.type === CHALLENGE_TYPES.APPLICATION ||
      rowData?.type === CHALLENGE_TYPES.PROGRAMMING ||
      rowData?.type === CHALLENGE_TYPES.NOTEBOOK ||
      rowData?.type === CHALLENGE_TYPES.SQL
        ? [
            {
              field: 'aiScore',
              minWidth: 150,
              headerName: 'AI Score',
              tooltip: TOOLTIPS.aiScoreColumnTooltip,
              renderCell: ({ row }) =>
                row?.aiScore == null ? (
                  <Typography sx={{ fontSize: '12px' }}>-</Typography>
                ) : (
                  <AIScoreComponent
                    acquired={row?.aiScore}
                    total={100}
                    challengeType={row?.type || null}
                    attemptId={rowData?.attemptId || null}
                    forChallengeRow
                  />
                )
            }
          ]
        : []),
      ...(rowData?.type === CHALLENGE_TYPES.PROGRAMMING
        ? [
            {
              flex: 0.15,
              minWidth: 170,
              field: 'linearityScore',
              headerName: 'Linearity Score',
              display: 'flex',
              tooltip: TOOLTIPS.linearityScore,
              renderCell: ({ row }) => {
                return row?.linearityScore == null ? (
                  <Typography sx={{ fontSize: '12px' }}>-</Typography>
                ) : (
                  <LinearityScoreComponent
                    acquired={row?.linearityScore}
                    total={row?.outOfScore}
                    challengeType={row?.stageType}
                    attemptId={row?.id}
                    forChallengeRow
                  />
                )
              }
            }
          ]
        : []),
      { field: 'languageLabel', headerName: 'Language' },
      { field: 'languageRuntimeLabel', headerName: 'Runtime Environment' },
      {
        field: 'submitTimestamp',
        headerName: 'Submit Time',
        renderCell: ({ row }) =>
          row?.submitTimestamp ? formatDateTime(row?.submitTimestamp) : '-'
      }
    ]
  }, [rowData])

  const childData = useMemo(() => {
    return rowData?.type === CHALLENGE_TYPES.MCQ
      ? rowData?.topicWiseMarkBreakDown || []
      : rowData?.results || []
  }, [rowData])

  return (
    <TableContainer
      sx={{
        border: '1px solid rgba(var(--customColors-mainRgb), 0.2)',
        borderBottom: '0'
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column?.field} sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {column?.headerName || ''}
                  {column?.tooltip ? (
                    <Icon
                      icon="mdi:information-outline"
                      title={column?.tooltip}
                      style={{
                        fontSize: '16px',
                        color: 'var(--text-secondary)'
                      }}
                    />
                  ) : null}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {childData.map((row) => {
            return (
              <ChildStageRow
                key={row?.challengeId || row?.topicId}
                row={row}
                columns={columns}
              />
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
})

const StageRow = memo(
  ({ row, memorisedColumns, handleCollapsedClick, collapsedAttemptIds }) => {
    // variables
    const isExpandable = useMemo(() => {
      const hasData =
        row?.type === CHALLENGE_TYPES.MCQ
          ? row?.topicWiseMarkBreakDown?.length > 0
          : row?.results?.length > 0

      return (
        hasData &&
        row?.type !== CHALLENGE_TYPES.INTERVIEW &&
        row?.type !== CHALLENGE_TYPES.PLACEMENT_TALK &&
        row?.type !== CHALLENGE_TYPES.PSYCHOMETRIC
      )
    }, [row])

    return (
      <Fragment key={row?.attemptId}>
        <TableRow
          {...(isExpandable
            ? { onClick: () => handleCollapsedClick(row?.attemptId) }
            : {})}
        >
          {memorisedColumns.map((column) => (
            <TableCell key={column?.field}>
              {column.renderCell
                ? column.renderCell({ row })
                : row[column?.field]}
            </TableCell>
          ))}
        </TableRow>

        {collapsedAttemptIds?.[row?.attemptId] && (
          <TableRow>
            <TableCell colSpan={memorisedColumns.length}>
              <ChildTable rowData={row} />
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    )
  }
)

const ChildStageRow = memo(({ row, columns }) => {
  return (
    <Fragment>
      <TableRow>
        {columns?.map((column) => (
          <TableCell key={column?.field}>
            {column.renderCell
              ? column.renderCell({ row })
              : row[column?.field]}
          </TableCell>
        ))}
      </TableRow>
    </Fragment>
  )
})

// Main Table Component
const CandidateStageHistory = ({
  title = null,
  data = [],
  loading = false,
  contestId,
  onRefresh,
  showRefreshButton = true,
  tableHeight = '100%',
  emptyStateMessage = 'No stage data available',
  assessment = false
}) => {
  // Hooks
  const { publicId } = useParams()

  // state
  const [collapsedAttemptIds, setCollapsedAttemptIds] = useState({})

  const isPublicRoute = useMemo(() => {
    return checkPublicRoute()
  }, [])

  // variables
  const { showExpandColumn, showActionsColumns } = useMemo(() => {
    let expand = false
    let actions = false

    for (const item of data || []) {
      const isInterviewOrTalk =
        item?.type === CHALLENGE_TYPES.INTERVIEW ||
        item?.type === CHALLENGE_TYPES.PSYCHOMETRIC ||
        item?.type === CHALLENGE_TYPES.PLACEMENT_TALK

      if (!isInterviewOrTalk) {
        actions = true

        const hasData =
          item?.type === CHALLENGE_TYPES.MCQ
            ? item?.topicWiseMarkBreakDown?.length > 0
            : item?.results?.length > 0

        if (hasData) {
          expand = true
        }
      }

      if (expand && actions) {
        break
      }
    }

    return { showExpandColumn: expand, showActionsColumns: actions }
  }, [data])

  const memorisedColumns = useMemo(() => {
    const baseColumns = [
      {
        field: 'stage',
        headerName: 'Stage Name',
        display: 'flex',
        minWidth: '180px',
        sortable: false,
        flex: 0.2
      },
      {
        field: 'duration',
        headerName: 'Attempt Duration',
        tooltip: TOOLTIPS.attemptDuration,
        display: 'flex',
        minWidth: '170px',
        flex: 0.2,
        sortable: true,
        renderCell: ({ row }) => {
          return <>{row?.duration != null ? `${row.duration} mins` : '-'}</>
        }
      },

      {
        field: 'score',
        headerName: 'Score',
        display: 'flex',
        minWidth: '100px',
        tooltip: TOOLTIPS.score,
        flex: 0.2,
        sortable: true,
        renderCell: ({ row }) => {
          return row?.resultStatus ===
            CANDIDATE_RESULT_STATUS.PENDING_MANUAL ? (
            <Typography
              sx={{
                color: 'var(--warning-main)',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              Yet to be scored
            </Typography>
          ) : row?.score == null ? (
            '-'
          ) : (
            <ScoreBadge acquired={row?.score} total={row?.outOfScore} />
          )
        }
      },

      {
        field: 'aiScore',
        minWidth: 150,
        headerName: 'AI Score',
        tooltip: TOOLTIPS.aiScoreColumnTooltip,
        renderCell: ({ row }) =>
          row?.aiScore == null ? (
            <Typography sx={{ fontSize: '12px' }}>-</Typography>
          ) : (
            <AIScoreComponent
              acquired={row?.aiScore}
              total={100}
              challengeType={row?.type || null}
              attemptId={row?.attemptId || null}
            />
          )
      },
      {
        flex: 0.15,
        minWidth: 170,
        field: 'linearityScore',
        headerName: 'Linearity Score',
        display: 'flex',
        tooltip: TOOLTIPS.linearityScore,
        renderCell: ({ row }) => {
          return row?.linearityScore == null ? (
            <Typography sx={{ fontSize: '12px' }}>-</Typography>
          ) : (
            <LinearityScoreComponent
              acquired={row?.linearityScore}
              total={row?.outOfScore}
              challengeType={row?.stageType}
              attemptId={row?.id}
              forChallengeRow
            />
          )
        }
      },

      {
        field: 'status',
        headerName: 'Status',
        display: 'flex',
        minWidth: '100px',
        flex: 0.2,
        sortable: false,
        renderCell: ({ row }) => {
          return (
            <Chip
              rounded
              size="small"
              label={ATTEMPT_STATUS_LABEL?.[row?.status]}
              color={
                row?.status === stageStatus?.DISQUALIFY ||
                row?.status === stageStatus?.REJECTED
                  ? 'error'
                  : row?.status === stageStatus?.IN_PROGRESS
                    ? 'warning'
                    : row?.status === stageStatus?.FINISHED
                      ? 'success'
                      : row?.status === stageStatus?.NO_SHOW
                        ? 'alert'
                        : 'default'
              }
            />
          )
        }
      },
      {
        field: 'startTimeStamp',
        headerName: 'Start time',
        display: 'flex',
        minWidth: '100px',
        sortable: true,
        flex: 0.2,
        renderCell: ({ row }) => (
          <>{row?.startTimeStamp ? formatDateTime(row?.startTimeStamp) : '-'}</>
        )
      }
    ]

    const actionsColumn = {
      field: 'actions',
      headerName: 'Actions',
      display: 'flex',
      minWidth: '50px',
      flex: 0.2,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <Box sx={{ width: 'max-content', height: '40px' }}>
            {shouldRenderPlaybackIcon(row?.type, row?.status) ? (
              <Link
                to={`${isPublicRoute ? `/share/${publicId}/` : '/'}${
                  assessment ? 'assessments' : 'contests'
                }/${contestId}/edit/stage/${row?.stageId}/${
                  row?.type === CHALLENGE_TYPES.VIDEO_RESPONSE
                    ? 'video-response-evaluate'
                    : row?.type === CHALLENGE_TYPES.MCQ
                      ? 'mcq-evaluate'
                      : row?.type === CHALLENGE_TYPES.PSYCHOMETRIC
                        ? 'psychometric-evaluate'
                        : row?.type === CHALLENGE_TYPES.AI_SCREENING
                          ? 'ai-screening-evaluate'
                          : 'evaluate'
                }/${row?.attemptId}`}
                state={{
                  pathname: `${window.location.pathname?.toLowerCase()}${
                    window.location.search
                  }`
                }}
                className="icon-link"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <Icon
                  title={TOOLTIPS.evaluate}
                  hoverEffect={true}
                  icon={'streamline:code-monitor-1-solid'}
                  fontSize={'24px'}
                />
              </Link>
            ) : (
              <></>
            )}
          </Box>
        )
      }
    }

    let finalColumns = [...baseColumns]

    if (showActionsColumns) {
      finalColumns.push(actionsColumn)
    }

    if (showExpandColumn) {
      finalColumns = [
        {
          minWidth: '30px',
          width: '30px',
          field: 'expand',
          headerName: '',
          renderCell: ({ row }) => (
            <Box sx={{ width: 'max-content' }}>
              {(row?.results?.length > 0 ||
                (row?.type === CHALLENGE_TYPES.MCQ &&
                  row?.topicWiseMarkBreakDown?.length > 0)) &&
              row?.type !== CHALLENGE_TYPES.INTERVIEW &&
              row?.type !== CHALLENGE_TYPES.PLACEMENT_TALK &&
              row?.type !== CHALLENGE_TYPES.PSYCHOMETRIC ? (
                <Icon
                  hoverEffect
                  icon={
                    !collapsedAttemptIds?.[row?.attemptId]
                      ? 'material-symbols:add'
                      : 'ic:baseline-minus'
                  }
                />
              ) : (
                <></>
              )}
            </Box>
          )
        },
        ...finalColumns
      ]
    }

    return finalColumns
  }, [
    publicId,
    showExpandColumn,
    showActionsColumns,
    contestId,
    collapsedAttemptIds,
    isPublicRoute,
    assessment
  ])

  // handlers
  const handleCollapsedClick = useCallback((attemptId) => {
    setCollapsedAttemptIds((prev) => {
      const data = { ...prev }
      if (data?.[attemptId]) {
        delete data[attemptId]
      } else {
        data[attemptId] = attemptId
      }
      return data
    })
  }, [])

  return (
    <Box sx={{ width: '100%', height: tableHeight }}>
      {showRefreshButton && onRefresh && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 8,
            py: 2
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {title}
          </Typography>
          <Icon
            onClick={onRefresh}
            icon={'mdi:refresh'}
            hoverEffect
            title="Refresh"
          />
        </Box>
      )}

      <TableContainer sx={{ height: tableHeight, maxHeight: tableHeight }}>
        <Table stickyHeader>
          <TableHead>
            <TableHeaderRow columns={memorisedColumns} />
          </TableHead>

          {loading ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={memorisedColumns.length} align="center">
                  <Box
                    sx={{
                      height: '100px',
                      minHeight: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <CircularProgress size={20} sx={{ marginTop: '6px' }} />
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {data?.map((row) => (
                <StageRow
                  key={row?.attemptId}
                  row={row}
                  memorisedColumns={memorisedColumns}
                  handleCollapsedClick={handleCollapsedClick}
                  collapsedAttemptIds={collapsedAttemptIds}
                />
              ))}

              {data?.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={memorisedColumns.length}
                    align="center"
                    sx={{ height: '100px', minHeight: '100px' }}
                  >
                    <Box
                      sx={{
                        height: '100px',
                        minHeight: '100px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {emptyStateMessage}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Box>
  )
}

export default memo(CandidateStageHistory)
